import { Component, inject, OnInit, OnDestroy, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowRequestsService } from '../../service/entity-services/workflow-requests.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { UsersService } from '../../service/entity-services/users.service';
import { AuthService } from '../../service/auth.service';
import { settledCache } from '../../shared/utils/cache-ready';
import { EntityActionsSidenavComponent, EntityAction } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';
import { WorkflowRequest, WORKFLOW_PHASES } from '../../objects/workflow';
import { WorkflowStatus } from '../../objects/interface-library';

const DOCUMENT_TYPES = ['contract', 'baa', 'coi', 'addendum', 'unknown'];
const TRANSACTION_TYPES = ['New', 'Renewal', 'Amendment'];
const PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'];

@Component({
  selector: 'app-new-workflow',
  templateUrl: './new-workflow.component.html',
  styleUrls: ['./new-workflow.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatSnackBarModule,
    MatAutocompleteModule,
    EntityActionsSidenavComponent
  ]
})
export class NewWorkflowComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private workflowService = inject(WorkflowRequestsService);
  private sidenavContentService = inject(SidenavContentService);
  private usersService = inject(UsersService);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);
  private destroy$ = new Subject<void>();

  @ViewChild('actionsSidenav') actionsSidenav!: MatSidenav;

  form!: FormGroup;
  loading = true;
  saving = false;
  isEditMode = false;
  existingRequest: WorkflowRequest | null = null;

  // Google Sheets data
  documentTypes: string[] = [];
  transactionTypes: string[] = [];
  phases: string[] = [];
  statuses: string[] = [];
  priorities: string[] = [];

  // Source entity context
  sourceEntityType = '';
  sourceEntityId = '';
  sourceRecord = '';

  // Workflow Request front-door: file handed in via router state.
  fromWorkflowRequest = false;
  prefilledFile: File | null = null;
  prefilledFileName = '';

  /** The signed-in user — stamped as requestor, and the default assignee on create. */
  currentUserEmail = '';
  currentUserName = '';

  /** Assignee picker (this component doesn't extend Entity, so it filters its own copy). */
  private allUsersList: any[] = [];
  filteredUsers: any[] = [];

  sidenavActions = signal<EntityAction[]>([
    {
      label: 'Save',
      icon: 'save',
      action: () => this.onSubmit(),
      disabled: false
    },
    {
      label: 'Cancel',
      icon: 'cancel',
      action: () => this.goBack(),
      disabled: false
    }
  ]);

  ngOnInit(): void {
    this.sidenavContentService.registerDetailSidenavToggle(() => {
      if (this.actionsSidenav) this.actionsSidenav.toggle();
    });

    this.initForm();
    this.loadSheetsData();
    this.loadCurrentUser();
    this.loadUserDirectory();

    // Workflow Request front-door: file + AI analysis arrive via router state.
    const navState = this.router.getCurrentNavigation()?.extras?.state || history.state;
    const prefilled = navState?.prefilledFile;
    if (prefilled instanceof File) {
      this.fromWorkflowRequest = true;
      this.prefilledFile = prefilled;
      this.prefilledFileName = prefilled.name;
      // TODO: map AI extraction -> workflow form fields. The dialog's own "create a
      // workflow request" path already does this end-to-end; this navigation path
      // still drops the extraction on the floor and makes the user retype it.
      console.log(
        '%c[NewWorkflow] Received from workflow-request dialog',
        'color:#ff6600;font-size:14px;font-weight:bold;',
        { file: prefilled, s3Url: navState?.prefilledFileS3Url, analysis: navState?.intakeAnalysisResult }
      );
    }

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.sourceEntityType = params['entityType'] || '';
      this.sourceEntityId = params['entityId'] || '';
      this.sourceRecord = params['sourceRecord'] || '';
    });

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.loadExisting(params['id']);
      } else {
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.sidenavContentService.unregisterDetailSidenavToggle();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: [''],
      documentType: [''],
      transactionType: [''],
      phase: [''],
      status: [''],
      priority: [''],
      assignedTo: [''],
      requestNotes: [''],
      dateRequested: [new Date()]
    });
  }

  private loadSheetsData(): void {
    // Workflow dropdowns come from hardcoded constants now (Google Sheets dependency removed).
    this.documentTypes = [...DOCUMENT_TYPES];
    this.transactionTypes = [...TRANSACTION_TYPES];
    this.phases = [...WORKFLOW_PHASES];
    this.statuses = Object.values(WorkflowStatus);
    this.priorities = [...PRIORITIES];
  }

  /**
   * Resolve the signed-in user. AuthService.getUserEmail() is the SSO-safe path — it
   * branches on environment.sso and decodes the Cognito ID token, which is what cbhi
   * needs. (UsersService.getCurrentUserEmail() goes through the Cognito SDK's
   * getCurrentUser() and does NOT work under the hosted-UI code flow.)
   */
  private async loadCurrentUser(): Promise<void> {
    try {
      const email = await this.authService.getUserEmail();
      this.currentUserEmail = email ?? '';
      if (this.currentUserEmail) {
        const name = this.usersService.getUserDisplayNameByEmail(this.currentUserEmail);
        this.currentUserName = name && name !== this.currentUserEmail ? name : '';
        // New requests default to the creator; edit mode overwrites this from the record.
        if (!this.isEditMode && !this.form.get('assignedTo')?.value) {
          this.form.patchValue({ assignedTo: this.currentUserEmail }, { emitEvent: false });
        }
      }
    } catch (e) {
      console.warn('[NewWorkflow] failed to resolve current user', e);
    }
  }

  private async loadUserDirectory(): Promise<void> {
    this.allUsersList = await settledCache(this.usersService.allUsers$ as any);
    this.filteredUsers = this.allUsersList;
  }

  /** Narrow the Assignee picker as the user types. Matches first name, last name, or email. */
  filterUsers(event: any): void {
    const query = (event?.target?.value ?? '').toLowerCase();
    this.filteredUsers = query
      ? this.allUsersList.filter(u => (u?.friendlyName ?? '').toLowerCase().includes(query))
      : [...this.allUsersList];
  }

  private loadExisting(workflowNum: string): void {
    this.workflowService.get(workflowNum).subscribe({
      next: (request) => {
        this.existingRequest = request;
        this.form.patchValue({
          name: request.name || '',
          documentType: request.documentType || '',
          transactionType: request.transactionType || '',
          phase: request.phase || '',
          status: request.status || '',
          priority: request.priority || '',
          assignedTo: request.assignedTo || '',
          requestNotes: request.requestNotes || '',
          dateRequested: request.dateRequested ? new Date(request.dateRequested) : new Date()
        });
        this.sourceEntityType = request.entityType || '';
        this.sourceEntityId = request.entityId || '';
        this.sourceRecord = request.contractFriendlyName || '';
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  onSubmit(): void {
    if (this.saving) return;
    this.saving = true;

    const formValue = this.form.value;
    const fields: Partial<WorkflowRequest> = {
      name: formValue.name,
      documentType: formValue.documentType,
      transactionType: formValue.transactionType,
      status: formValue.status,
      priority: formValue.priority,
      assignedTo: this.emailOf(formValue.assignedTo),
      requestNotes: formValue.requestNotes,
      dateRequested: formValue.dateRequested?.toISOString() || new Date().toISOString(),
      entityType: this.sourceEntityType,
      entityId: this.sourceEntityId,
      contractFriendlyName: this.sourceRecord,
      // Sent on BOTH paths, not just create: the backend stamps phaseHistory.enteredBy
      // from `requestor`, so omitting it on update left every edit's history entry blank.
      requestor: this.currentUserEmail || this.existingRequest?.requestor || '',
      requestorName: this.currentUserName || this.existingRequest?.requestorName || ''
    };

    // `phase` is intentionally NOT sent from edit mode — see the phase note on
    // loadSheetsData/the template. Phase moves go through the detail view's
    // Advance / Return actions, which respect the backend's ±1 rule.
    if (!this.isEditMode) {
      fields.phase = formValue.phase;
    }

    const request$ = this.existingRequest?.workflowNum
      ? this.workflowService.update(this.existingRequest.workflowNum, fields)
      : this.workflowService.create(fields);

    request$.subscribe({
      next: () => {
        this.snackBar.open('Workflow request saved', 'OK', { duration: 3000 });
        this.saving = false;
        this.goBack();
      },
      error: () => {
        // Error snackbar is already surfaced by the service
        this.saving = false;
      }
    });
  }

  /** The Assignee autocomplete emits a user object; the record stores a plain email. */
  private emailOf(value: any): string {
    if (!value) return '';
    return typeof value === 'string' ? value : (value.email ?? '');
  }

  /** Renders the picked user in the Assignee input. */
  displayUserFn(user: any): string {
    if (!user) return '';
    return typeof user === 'string' ? user : (user.shortFriendlyName || user.email || '');
  }

  goBack(): void {
    window.history.back();
  }
}
